import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Heart, Users, Compass, ExternalLink } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { QualityAssurance } from "@/components/QualityAssurance";
import { LabGallery } from "@/components/LabGallery";
import { MeetTheTeam } from "@/components/MeetTheTeam";
import { CONTACT } from "@/lib/siteData";
import { BRAND } from "@/lib/brand";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";

const VALUES = [
  {
    icon: Heart,
    title: "Answers, not just numbers",
    desc: "Every report includes a plain-English summary so you know what was found, what it means and what to do next.",
  },
  {
    icon: Compass,
    title: "Driven by your water",
    desc: "We don't sell water filters or treatment systems. Our recommendations are driven by your water — not a product catalogue.",
  },
  {
    icon: Users,
    title: "Built for real customers",
    desc: "Homeowners, property managers, plumbers and rural property owners — not regulators. We design our reports for the people who actually read them.",
  },
];

export default function About() {
  useSEO({
    title: "About Aware Water Testing — Independent Lab, Clear Answers",
    description: "Aware Water Testing is an independent Australian water testing laboratory based in Noosaville, Queensland. Plain-English reports and practical recommendations for tank, bore and drinking water — supporting customers Australia-wide.",
    url: `${SEO_DEFAULTS.baseUrl}/about`,
    type: "website",
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-28 lg:pt-36 pb-14 lg:pb-20 overflow-hidden">
          <div className="absolute top-32 right-10 w-72 h-72 bg-cyan-400/10 rounded-full blur-3xl -z-10" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-sky-300/10 rounded-full blur-3xl -z-10" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600">About Us</span>
              <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05]">
                Local water testing. <span className="water-text">Clear answers.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                Aware Water Testing is an independent Australian water testing laboratory based in Noosaville, Queensland. We support homeowners, trade professionals, rural property owners and businesses <strong>Australia-wide</strong> with <strong>fast turnaround, professional reporting and practical recommendations</strong> they can actually act on.
              </p>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                Local drop-off is available at our Noosaville lab, and posted / courier sample options are supported for customers across Australia. Most laboratories give results — we provide answers, combining recognised laboratory methodologies with a plain-English summary report on every test.
              </p>
              {/* Jun 2026 — rebrand continuity. A visible "formerly known
                  as" statement is the clearest signal to both returning
                  customers and search engines that this is the same
                  business under a new name, not a new company. Pairs with
                  the schema.org `alternateName`. */}
              {BRAND.formerName && (
                <p
                  data-testid="about-former-name"
                  className="mt-6 rounded-xl border border-slate-200 bg-white/70 px-4 py-3 text-sm text-slate-600 leading-relaxed"
                >
                  <strong className="text-slate-900">
                    {BRAND.name} was formerly known as {BRAND.formerName}.
                  </strong>{" "}
                  Same laboratory, same Noosaville team and the same
                  Australian phone number — we changed our name in 2026 as
                  we began supporting customers Australia-wide rather than
                  the Sunshine Coast alone. If you tested with us under the
                  old name, your history is unchanged.
                </p>
              )}
            </motion.div>
          </div>
        </section>

        {/* Story / 2 col */}
        <section className="py-14 lg:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="lg:col-span-6">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600">Our story</span>
                <h2 className="font-display mt-3 text-3xl lg:text-4xl font-bold text-slate-900 leading-[1.1]">
                  Why we started this lab
                </h2>
                <div className="mt-5 space-y-4 text-slate-700 text-[15px] leading-relaxed">
                  <p>For years we watched friends and neighbours on the Sunshine Coast struggle with the same problem: they'd send a water sample off, wait weeks, then receive a spreadsheet of numbers that meant nothing to them.</p>
                  <p>Worse — when something <em>was</em> wrong, they had no idea what to do next. The lab told them they had iron in their water. Then what?</p>
                  <p>So we built a different kind of laboratory: fast, plain-English, and obsessively focused on the customer experience. Every report we issue has two parts — the professional Certificate of Analysis and a plain-English summary that tells you exactly what was found, what it means, and whether action is required. Today we still call Noosaville home, but we serve customers Australia-wide through our posted-sample pathway.</p>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="rounded-3xl bg-white border border-slate-100 p-7 lg:p-9 shadow-sm">
                  <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600 mb-4">By the numbers</div>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="font-display text-4xl font-bold text-slate-900">2,000+</div>
                      <div className="text-sm text-slate-500 mt-1">Reports issued</div>
                    </div>
                    <div>
                      <div className="font-display text-4xl font-bold text-slate-900">1–3</div>
                      <div className="text-sm text-slate-500 mt-1">Days typical turnaround</div>
                    </div>
                    <div>
                      <div className="font-display text-4xl font-bold text-slate-900">National</div>
                      <div className="text-sm text-slate-500 mt-1">Reach — posted samples Australia-wide</div>
                    </div>
                    <div>
                      <div className="font-display text-4xl font-bold text-slate-900">5.0★</div>
                      <div className="text-sm text-slate-500 mt-1">Customer satisfaction</div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 rounded-3xl bg-slate-900 text-white p-7 lg:p-9">
                  <MapPin className="w-6 h-6 text-cyan-400 mb-3" />
                  <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-400">Visit the lab</div>
                  <p className="mt-2 text-white font-display text-lg leading-snug">{CONTACT.address}</p>
                  <p className="text-sm text-slate-300 mt-1">Open Mon–Fri · {CONTACT.phone}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-14 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600">What we stand for</span>
              <h2 className="font-display mt-3 text-3xl lg:text-4xl font-bold text-slate-900 leading-[1.1]">
                Three principles that shape every test we run.
              </h2>
            </div>
            <div className="mt-10 grid md:grid-cols-3 gap-5">
              {VALUES.map((v, i) => {
                const Icon = v.icon;
                return (
                  <motion.div
                    key={v.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: i * 0.08 }}
                    className="rounded-3xl bg-slate-50 border border-slate-100 p-7 hover:border-cyan-200 hover:bg-white hover:shadow-lg transition-all"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-white border border-slate-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-cyan-600" />
                    </div>
                    <h3 className="mt-5 font-display text-lg font-semibold text-slate-900">{v.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Specialist referral — Pristine Water Systems */}
        <section className="py-14 lg:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600">Specialist referral</span>
                <h2 className="font-display mt-3 text-3xl lg:text-4xl font-bold text-slate-900 leading-[1.1]">
                  When your water needs treatment, you'll know who to call.
                </h2>
                <p className="mt-5 text-slate-700 text-[15px] leading-relaxed">
                  Sometimes the answer to a water issue is more than information — it's installation, treatment or filtration. When our analysis indicates that a treatment system may help, customers can request a free <strong>Pristine Water Systems Sunshine Coast</strong> estimate or assessment directly from their Summary Report.
                </p>
                <p className="mt-3 text-slate-700 text-[15px] leading-relaxed">
                  Pristine Water Systems are specialists in rainwater, bore water and rural property water-treatment solutions across the Sunshine Coast — and they know exactly how to translate a lab report into the right hardware for your home.
                </p>
                <p className="mt-3 text-sm text-slate-500 leading-relaxed">
                  Referrals are only made where our test results suggest treatment may genuinely help. There is no obligation, and we don't sell hardware ourselves — so the lab analysis stays focused on the result.
                </p>

                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/book-water-test"
                    className="btn-aqua group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold"
                  >
                    Book a water test <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <a
                    href="https://pristinewatersystems.com.au"
                    target="_blank" rel="noopener noreferrer"
                    data-testid="about-pws-link"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold text-slate-900 bg-white border-2 border-slate-200 hover:border-slate-900 transition-all"
                  >
                    Pristine Water Systems <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="relative rounded-3xl bg-slate-900 text-white p-8 overflow-hidden">
                  <div className="absolute -top-24 -right-24 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl" />
                  <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-sky-500/15 rounded-full blur-3xl" />
                  <div className="relative">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 font-bold">How the referral works</div>
                    <ol className="mt-5 space-y-4">
                      {[
                        ["01", "We test your water and produce a plain-English report"],
                        ["02", "Our lab team reviews results and flags whether treatment may help"],
                        ["03", "We connect you with Pristine Water Systems Sunshine Coast — no obligation"],
                        ["04", "They quote, you decide. We stay focused on the test result."],
                      ].map(([n, t]) => (
                        <li key={n} className="flex items-start gap-3.5">
                          <span className="font-display text-cyan-400 font-bold text-base w-7 pt-0.5">{n}</span>
                          <span className="text-sm text-slate-200 leading-snug">{t}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reuse QA section */}
        <QualityAssurance />

        {/* Lab gallery */}
        <LabGallery />

        {/* Team */}
        <MeetTheTeam />

        {/* CTA */}
        <section className="py-14 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative rounded-[2.5rem] bg-slate-900 text-white p-10 sm:p-14 lg:p-20 overflow-hidden">
              <div className="absolute -top-32 -right-32 w-96 h-96 bg-cyan-500/25 rounded-full blur-3xl" />
              <div className="relative max-w-2xl">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400">Get clarity on your water</span>
                <h2 className="font-display mt-3 text-3xl sm:text-5xl font-bold leading-[1.05]">
                  Ready when you are.
                </h2>
                <p className="mt-4 text-slate-300 text-lg max-w-xl">
                  Book online for one flat all-inclusive price and get a plain-English answer about your water within days.
                </p>
                <Link to="/book-water-test" className="btn-aqua mt-7 inline-flex items-center gap-2 px-7 py-4 rounded-full text-base font-semibold">
                  Book online <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
